import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import debounce from 'lodash/debounce';

type JsonResponse = {
  error: boolean;
  results: {
    facets: unknown;
    hits: object[];
    name: string;
    offset: number;
    total: number;
    totalByMode: Record<string, number>;
  }[];
};

const DEFAULT_QUERY_OPTIONS = {
  groups: [
    {
      limit: 10,
      name: 'categories',
      modes: [
        {
          name: 'brand_category',
          params: {
            fq: 'row_type:category OR row_type:virtual_category OR row_type:brand_virtual_category',
          },
        },
      ],
    },
    {
      limit: 15,
      name: 'products',
      modes: ['product', 'raw_sorted', 'raw'],
    },
    {
      limit: 10,
      name: 'brands',
      modes: ['brand'],
    },
    {
      limit: 10,
      name: 'stores',
      modes: ['store'],
    },
  ],
};

type SearchContainerProps = {
  render: (results: unknown) => React.ReactElement;
  renderLoader?: () => React.ReactElement;
  query: string;
  queryOptions?: {
    groups: {
      limit: number;
      name: string;
      modes: (string | { name: string; params: Record<string, string> })[];
    }[];
  };
  disableScrollToTop: boolean;
  onSearchResultFound?: (results: unknown[]) => void;
};

const SearchContainer: React.FC<SearchContainerProps> = ({
  query,
  queryOptions = DEFAULT_QUERY_OPTIONS,
  onSearchResultFound,
  disableScrollToTop,
  render,
  renderLoader,
}) => {
  const [results, setResults] = useState<unknown[]>([]);
  const [error, setError] = useState<boolean>(false);
  const { market } = useAppContext();

  const selectionId = useRef(0);
  const retrySearch = useRef<any>(-1);
  const mounted = useRef(false);

  const search = useCallback(
    debounce(() => {
      setError(false);

      if (!market || !market.superSearchEndpoint || !market.code) {
        // eslint-disable-next-line no-console
        console.error(
          'No superSearchEndpoint is configured in <MarketContext>'
        );
        return;
      }

      const url = market.superSearchEndpoint;
      const body = {
        market: market.code,
        query,
        groups: queryOptions.groups,
      };

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(resp => {
          if (resp.status >= 400 && resp.status < 600) {
            throw new Error((resp as any).message);
          }
          return resp.json();
        })
        .then((json: JsonResponse) => {
          if (json.error) {
            throw new Error('Error in search container');
          }

          const groups = json.results.map(group =>
            Object.assign(group, {
              hits: group.hits.map(item => {
                selectionId.current += 1;
                return {
                  ...item,
                  selectionId: `sc${selectionId.current}`,
                };
              }),
              offset: 0,
            })
          );

          clearTimeout(retrySearch.current);

          if (mounted.current) {
            setResults(prev => (body.query === query ? groups : prev));
          }

          if (!disableScrollToTop) {
            // Reset scroll position for small devices
            if (IS_BROWSER && window.innerWidth <= 620) {
              window.scrollTo(0, 0);
            }
          }
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error(e);

          clearTimeout(retrySearch.current);
          setError(true);

          if (mounted.current) {
            // If error occurs, retry search after 1000s
            retrySearch.current = setTimeout(() => {
              search();
            }, 1000);
          }
        });
    }, 300),
    [query, queryOptions, disableScrollToTop, market]
  );

  useEffect(() => {
    search();

    return () => {
      search.cancel();
    };
  }, [search]);

  useEffect(() => {
    onSearchResultFound?.(results);
  }, [results, onSearchResultFound]);

  useEffect(() => {
    mounted.current = true;
  }, []);

  if (!results.length) {
    return renderLoader?.() ?? null;
  }

  if (error) {
    return null;
  }

  return render(results);
};

export default SearchContainer;
